import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadIcon, SparklesIcon, DownloadIcon, CheckIcon, FileSpreadsheetIcon, DatabaseIcon } from 'lucide-react';
const tabs = [{
  id: 'upload',
  icon: UploadIcon,
  title: 'Find or upload local business records',
  description: 'Search for local businesses directly in Openmart, upload a CSV of your existing records, or connect your CRM. Our local business data enrichment engine prepares your records instantly.'
}, {
  id: 'enrich',
  icon: SparklesIcon,
  title: 'Enrich local business contacts',
  description: 'Automatically enrich each local business record with verified owner emails, direct phones, tech stacks, social profiles, revenue estimates, and 50+ data fields.'
}, {
  id: 'export',
  icon: DownloadIcon,
  title: 'Export or sync enriched data',
  description: 'Push enriched local business contact data directly to Salesforce, HubSpot, or download as CSV. Every record is structured, deduplicated, and ready for outreach.'
}];
export function EnrichmentWorkflow() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-2956">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2957">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2958">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-2959">
            How local business data{' '}
            <span className="text-blue-600" data-id="element-2960">enrichment</span> works
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-2961">
            From incomplete records to fully enriched local business profiles in
            minutes.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-2962">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-2963">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-2964">
                  <div className="flex items-start gap-6" data-id="element-2965">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-2966">
                      <tab.icon className="w-6 h-6" data-id="element-2967" />
                    </div>
                    <div data-id="element-2968">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-2969">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-2970">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-2971">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-2972">
              <AnimatePresence mode="wait" data-id="element-2973">
                {activeTab === 'upload' && <motion.div key="upload" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center" data-id="element-2974">
                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-2975">
                      <FileSpreadsheetIcon className="w-8 h-8 text-blue-600" data-id="element-2976" />
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2" data-id="element-2977">
                      Two ways to get started
                    </h4>
                    <p className="text-sm text-slate-500 mb-6" data-id="element-2978">
                      Choose how you want to add local business records
                    </p>

                    <div className="space-y-3 text-left" data-id="element-2979">
                      <div className="bg-slate-50 rounded-xl p-4 border-2 border-blue-100" data-id="element-2980">
                        <div className="flex items-center gap-3" data-id="element-2981">
                          <DatabaseIcon className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-2982" />
                          <div data-id="element-2983">
                            <p className="text-sm font-semibold text-slate-900" data-id="element-2984">
                              Search in Openmart
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-2985">
                              Find local businesses by industry and location
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-2986">
                        <div className="flex items-center gap-3" data-id="element-2987">
                          <FileSpreadsheetIcon className="w-5 h-5 text-slate-500 flex-shrink-0" data-id="element-2988" />
                          <div data-id="element-2989">
                            <p className="text-sm font-semibold text-slate-900" data-id="element-2990">
                              Upload a CSV
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-2991">
                              Import your existing local business records
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'enrich' && <motion.div key="enrich" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-xl" data-id="element-2992">
                    <div className="grid grid-cols-2 gap-4" data-id="element-2993">
                      {/* Before */}
                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 opacity-75" data-id="element-2994">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-2995">
                          Input record
                        </div>
                        <div className="space-y-3" data-id="element-2996">
                          <div data-id="element-2997">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-2998">
                              Business name
                            </div>
                            <div className="text-sm font-medium text-slate-700" data-id="element-2999">
                              Smith Plumbing
                            </div>
                          </div>
                          <div data-id="element-3000">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-3001">
                              Address
                            </div>
                            <div className="text-sm font-medium text-slate-700" data-id="element-3002">
                              Austin, TX
                            </div>
                          </div>
                          <div className="pt-2 border-t border-slate-50" data-id="element-3003">
                            <div className="text-xs text-slate-400 italic" data-id="element-3004">
                              No email available
                            </div>
                            <div className="text-xs text-slate-400 italic mt-1" data-id="element-3005">
                              No owner info
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* After */}
                      <div className="bg-white rounded-2xl p-5 shadow-md border-2 border-blue-100 relative" data-id="element-3006">
                        <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center border-2 border-white z-10" data-id="element-3007">
                          <SparklesIcon className="w-3 h-3 text-white" data-id="element-3008" />
                        </div>
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4 flex items-center gap-1" data-id="element-3009">
                          <CheckIcon className="w-3 h-3" data-id="element-3010" /> Enriched profile
                        </div>
                        <div className="space-y-3" data-id="element-3011">
                          <div data-id="element-3012">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-3013">
                              Business name
                            </div>
                            <div className="text-sm font-medium text-slate-900" data-id="element-3014">
                              Smith Plumbing & HVAC LLC
                            </div>
                          </div>
                          <div data-id="element-3015">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-3016">
                              Owner contact
                            </div>
                            <div className="text-sm font-medium text-slate-900 flex items-center gap-1" data-id="element-3017">
                              <CheckIcon className="w-3 h-3 text-green-500" data-id="element-3018" />{' '}
                              John Smith
                            </div>
                          </div>
                          <div data-id="element-3019">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-3020">
                              Verified email
                            </div>
                            <div className="text-sm font-medium text-blue-600 flex items-center gap-1" data-id="element-3021">
                              <CheckIcon className="w-3 h-3 text-green-500" data-id="element-3022" />{' '}
                              john@smithplumbing.com
                            </div>
                          </div>
                          <div data-id="element-3023">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-3024">
                              Direct phone
                            </div>
                            <div className="text-sm font-medium text-slate-900 flex items-center gap-1" data-id="element-3025">
                              <CheckIcon className="w-3 h-3 text-green-500" data-id="element-3026" />{' '}
                              (512) 555-0198
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'export' && <motion.div key="export" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md" data-id="element-3027">
                    <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100" data-id="element-3028">
                      <h4 className="font-bold text-slate-900 mb-6 text-center" data-id="element-3029">
                        Export enriched local business data
                      </h4>

                      <div className="space-y-3 mb-8" data-id="element-3030">
                        <div className="flex items-center justify-between p-4 rounded-xl border-2 border-blue-600 bg-blue-50 cursor-pointer" data-id="element-3031">
                          <div className="flex items-center gap-3" data-id="element-3032">
                            <DatabaseIcon className="w-5 h-5 text-blue-600" data-id="element-3033" />
                            <span className="font-semibold text-blue-900" data-id="element-3034">
                              Salesforce
                            </span>
                          </div>
                          <div className="w-4 h-4 rounded-full border-4 border-blue-600 bg-white" data-id="element-3035"></div>
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors" data-id="element-3036">
                          <div className="flex items-center gap-3" data-id="element-3037">
                            <DatabaseIcon className="w-5 h-5 text-slate-600" data-id="element-3038" />
                            <span className="font-medium text-slate-700" data-id="element-3039">
                              HubSpot
                            </span>
                          </div>
                          <div className="w-4 h-4 rounded-full border border-slate-300" data-id="element-3040"></div>
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors" data-id="element-3041">
                          <div className="flex items-center gap-3" data-id="element-3042">
                            <FileSpreadsheetIcon className="w-5 h-5 text-slate-600" data-id="element-3043" />
                            <span className="font-medium text-slate-700" data-id="element-3044">
                              Download CSV
                            </span>
                          </div>
                          <div className="w-4 h-4 rounded-full border border-slate-300" data-id="element-3045"></div>
                        </div>
                      </div>

                      <button className="w-full py-3.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors flex items-center justify-center gap-2" data-id="element-3046">
                        <DownloadIcon className="w-4 h-4" data-id="element-3047" /> Sync 9,824 records
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}